import React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ITool } from '@/interfaces/ITool';
import { cn } from '@/lib/utils';

export type ToolAttribute = ITool['attributes'][0];

export interface AttributeInputProps {
  attr: ToolAttribute;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  compact?: boolean;
}

export const AttributeInput: React.FC<AttributeInputProps> = ({ attr, value, onChange, compact = false }) => {
  const commonClassName = compact ? 'h-8 text-xs px-2' : '';

  switch (attr.type) {
    case 'string':
      if (attr.multiline) {
        return (
          <textarea
            className={cn(
              'w-full min-h-32 p-3 bg-background border border-border rounded-md resize-y font-mono text-sm leading-5 focus:outline-none',
              commonClassName
            )}
            value={(value as string) ?? (attr.defaultValue as string) ?? ''}
            onChange={(e) => onChange(attr.name, e.target.value)}
            spellCheck={false}
          />
        );
      }
      return (
        <Input
          type="text"
          className={commonClassName}
          value={(value as string) ?? (attr.defaultValue as string) ?? ''}
          onChange={(e) => onChange(attr.name, e.target.value)}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          className={commonClassName}
          value={(value as number) ?? (attr.defaultValue as number) ?? 0}
          onChange={(e) => onChange(attr.name, parseInt(e.target.value, 10))}
        />
      );

    case 'boolean':
      return (
        <Checkbox
          checked={(value as boolean) ?? (attr.defaultValue as boolean) ?? false}
          onCheckedChange={(checked) => onChange(attr.name, checked === true)}
        />
      );

    case 'select': {
      const selectValue = (value as string) ?? (attr.defaultValue as string) ?? '';
      return (
        <Select
          value={selectValue === '' ? '__empty__' : selectValue}
          onValueChange={(val) => onChange(attr.name, val === '__empty__' ? '' : val)}
        >
          <SelectTrigger className={cn('w-full', commonClassName)}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {selectValue === '' && (
              <SelectItem value="__empty__" className="hidden">
                Select...
              </SelectItem>
            )}
            {(attr.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    default:
      return null;
  }
};
